import { RETRY_OR_SUPPORT_MESSAGE } from '@shared/i18n';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { CustomMessageBar, StatusLabel } from '@shared/components';
import {
  DETECTIONS_TABLE_COLUMN_IDS,
  DETECTIONS_TABLE_SIZING_PARAMS,
  INLINE_BLOCK_DETECTION_PREVIEW_TABLE_ID,
} from '../../constants';
import {
  useCancelInlineBlockDetection,
  usePollInlineBlockDetection,
  useSubmitInlineBlockDetection,
} from '../../api/inlineBlocks.api';
import { EDetectionPollingStatus, EDetectionViewMode } from '../../types/shared.types';
import { TItem, TResolvedItemsMap } from '@modules/items';
import { IInlineBlockDetectionRun } from '../../types/inlineBlocks.types';
import DetectionsTable from '../shared/tables/DetectionsTable';
import PollingDialog from '../shared/dialogs/PollingDialog';
import { groupByItem, mapToObject } from '../../helpers/group.helper';
import useDetectionPreviewLifecycle from '../../hooks/useDetectionPreviewLifecycle.hook';
import useDetectionsColumns from '../../hooks/useDetectionsColumns.hook';
import PreviewActionsBar from '../shared/layout/PreviewActionsBar';
import PreviewHeader from '../shared/layout/PreviewHeader';
import useCheckedRows from '../../hooks/useCheckedRows.hook';

type TProps = {
  runId: string;
  setRunId: (id: string | undefined) => void;
  handleStart: () => void;
  setSelectedItems: Dispatch<SetStateAction<TItem[]>>;
};

const InlineBlockDetectionPreview = ({ runId, setRunId, setSelectedItems, handleStart }: TProps) => {
  const {
    pollQuery,
    run,
    resolvedItems,
    isAnalysisFailed,
    isPollingDialogOpen,
    setIsPollingDialogOpen,
    openPollingDialog,
    cancelMutation,
    handleCancelDetection,
  } = useDetectionPreviewLifecycle<IInlineBlockDetectionRun>({
    runId,
    usePoll: usePollInlineBlockDetection,
    useCancel: useCancelInlineBlockDetection,
    onCancelDone: () => {
      setRunId(undefined);
      setSelectedItems([]);
    },
    leaveGuardMessage: 'Your library items aren’t inserted yet. Leaving will cancel the run. Proceed?',
    summaryNavigateTo: (id) => `${id}/summary`,
  });

  return (
    <>
      <PollingDialog
        isOpen={isPollingDialogOpen}
        setIsOpen={setIsPollingDialogOpen}
        pollQuery={pollQuery}
        isAnalysisFailed={isAnalysisFailed}
        label="library items"
      />

      <StatusLabel
        data={pollQuery.data && pollQuery.data.status === EDetectionPollingStatus.READY ? pollQuery.data : undefined}
        loadingMessage="Loading library items..."
        error={pollQuery.error}
        errorMessage="Loading library items failed!"
        render={(resolvedData) => (
          <PreviewContent
            run={resolvedData.run}
            resolvedItems={resolvedData.resolvedItems}
            isAnalysisFailed={isAnalysisFailed}
            cancelMutation={cancelMutation}
            handleCancelDetection={handleCancelDetection}
            handleStart={handleStart}
            openPollingDialog={openPollingDialog}
            targetDate={resolvedData.targetDate}
          />
        )}
      />
    </>
  );
};

type InnerProps = {
  run: IInlineBlockDetectionRun;
  resolvedItems: TResolvedItemsMap;
  isAnalysisFailed: boolean;
  cancelMutation: ReturnType<typeof useCancelInlineBlockDetection>;
  handleCancelDetection: () => void;
  handleStart: () => void;
  openPollingDialog: () => void;
  targetDate: string;
};

const PreviewContent = ({
  run,
  resolvedItems,
  isAnalysisFailed,
  cancelMutation,
  handleCancelDetection,
  handleStart,
  openPollingDialog,
  targetDate,
}: InnerProps) => {
  // Group by document id
  const grouped = useMemo(() => mapToObject(groupByItem(run.detectedInlineBlocks)), [run.detectedInlineBlocks]);

  // Checkboxes
  const { checkedRowIds, setCheckedRowIds } = useCheckedRows(run.detectedInlineBlocks);

  // Submit
  const submitMutation = useSubmitInlineBlockDetection(run._id);
  const handleInsertInlineBlocks = () => {
    const selected = run.detectedInlineBlocks.filter((r) => checkedRowIds.has(r.id)).map((r) => ({ id: r.id }));
    if (selected.length === 0) return;
    submitMutation.mutate({ chosenInlineBlocks: selected }, { onSuccess: openPollingDialog });
  };

  const columnSizing = useDetectionsColumns<{ id: string }>(
    INLINE_BLOCK_DETECTION_PREVIEW_TABLE_ID,
    DETECTIONS_TABLE_COLUMN_IDS,
    DETECTIONS_TABLE_SIZING_PARAMS
  );

  return (
    <div className="flex flex-col space-y-6">
      <PreviewHeader
        rightDisabled={submitMutation.isPending || cancelMutation.isPending}
        onRestart={handleStart}
        targetDate={targetDate}
      />

      <div className="flex flex-col space-y-4">
        {isAnalysisFailed ? (
          <CustomMessageBar intent="error" title="Error analyzing library items." body={RETRY_OR_SUPPORT_MESSAGE} />
        ) : Object.keys(grouped).length > 0 ? (
          Object.entries(grouped).map(([docId, detectedInlineBlocks]) => (
            <DetectionsTable
              key={docId}
              variant="inline-blocks"
              columnSizing={columnSizing}
              sourceId={docId}
              detectedElements={detectedInlineBlocks}
              checkedRowIds={checkedRowIds}
              setCheckedRowIds={setCheckedRowIds}
              resolvedItems={resolvedItems}
            />
          ))
        ) : (
          <CustomMessageBar
            intent="info"
            title="No library items found."
            body="Please make sure you have selected the correct folders and run the detection again."
          />
        )}
      </div>

      <PreviewActionsBar
        onCancel={handleCancelDetection}
        onInsert={handleInsertInlineBlocks}
        cancelLoading={cancelMutation.isPending}
        insertLoading={submitMutation.isPending}
        insertDisabled={checkedRowIds.size === 0}
        count={checkedRowIds.size}
        nounSingular="library item"
        nounPlural="library items"
        targetDate={targetDate}
      />
    </div>
  );
};

export default InlineBlockDetectionPreview;
